import { useState, useEffect } from 'react';
import {
  Link as LinkIcon,
  Filter,
  Menu as MenuIcon,
  MessageCircle,
  ArrowLeft,
} from 'lucide-react';
import { api, ParsedUserProfile, PortfolioItem } from '../../lib/api';
import { getTelegramWebApp } from '../../lib/telegram';
import { PortfolioMiniCard } from './PortfolioMiniCard';
import { PortfolioSwipeCardModal } from './PortfolioSwipeCardModal';

interface UserProfileModalProps {
  profile: ParsedUserProfile & { id?: string };
  onClose: () => void;
  hasActiveFilters?: boolean;
  onChangeFilters?: () => void;
  isListener?: boolean;
  onItemClick?: (item: PortfolioItem) => void;
  onMessageClick?: (userId: number, username?: string) => void;
  onWrite?: (item: PortfolioItem) => void;
}

const ROLE_LABELS: Record<string, string> = {
  listener: 'Слухач',
  beatmaker: 'Бітмейкер',
  producer: 'Продюсер',
  designer: 'Дизайнер',
  performer: 'Виконавець',
  director: 'Режисер',
};

export function UserProfileModal({
  profile,
  onClose,
  hasActiveFilters = false,
  onChangeFilters,
  isListener = false,
  onItemClick,
  onMessageClick,
  onWrite,
}: UserProfileModalProps) {
  const [isFollowing, setIsFollowing] = useState(false);
  const [selectedPortfolioItem, setSelectedPortfolioItem] = useState<PortfolioItem | null>(null);

  const portfolios = profile.portfolios ?? [];
  const stats = profile.stats;
  const totalWorks = portfolios.length;
  const totalLikes = stats?.total_likes ?? stats?.profile_likes ?? 0;
  const followers = stats?.followers ?? 0;

  // Fetch initial follow status
  useEffect(() => {
    let mounted = true;
    if (profile?.telegram_id) {
      api.user.getFollowStatus(profile.telegram_id)
        .then((res) => {
          if (mounted) setIsFollowing(res.is_following);
        })
        .catch((e) => console.warn('Failed to fetch follow status:', e));
    }
    return () => {
      mounted = false;
    };
  }, [profile?.telegram_id]);

  const handleFollowToggle = async () => {
    // Optimistic toggle for snappy UI
    const newStatus = !isFollowing;
    setIsFollowing(newStatus);
    try {
      // Use telegram_id as a backend identifier
      if (profile?.telegram_id) {
        if (newStatus) {
          await api.user.follow(profile.telegram_id);
        } else {
          await api.user.unfollow(profile.telegram_id);
        }
      }
    } catch (e) {
      // Revert on failure silently
      setIsFollowing((prev) => !prev);
      console.warn('Follow toggle failed:', e);
    }
  };

  const handleMessageClick = () => {
    if (onMessageClick && profile?.telegram_id) {
      // Use the provided callback to navigate to chats
      onMessageClick(profile.telegram_id, profile.username || profile.name || undefined);
    } else {
      // Fallback to opening Telegram link if no callback provided
      const tg = getTelegramWebApp();
      if (!tg) return;
      try {
        if (profile?.username) {
          tg.openTelegramLink(`https://t.me/${profile.username}`);
        } else if (profile?.telegram_id) {
          tg.openTelegramLink(`tg://user?id=${profile.telegram_id}`);
        }
      } catch (e) {
        console.warn('Failed to open Telegram link:', e);
      }
    }
  };


  // Helper to check if portfolio item should be hidden for listeners
  const isLinkHidden = (item: PortfolioItem) => item.type === 'link' && isListener;

  return (
    <div className="fixed inset-0 z-50 min-h-screen w-full overflow-y-auto"
         style={{
           background: 'linear-gradient(180deg, rgba(15, 20, 25, 0.98) 0%, rgba(10, 15, 20, 0.98) 100%)',
         }}>
        
        {/* Header Buttons */}
        <div className="px-4 pt-4 pb-2 flex items-center justify-between">
          <button
            onClick={onClose}
            className="w-10 h-10 rounded-full border backdrop-blur-xl flex items-center justify-center transition-all hover:border-white/30"
            style={{
              background: 'rgba(255, 255, 255, 0.05)',
              borderColor: 'rgba(255, 255, 255, 0.12)',
            }}
          >
            <ArrowLeft className="w-5 h-5" style={{ color: 'rgba(255, 255, 255, 0.7)' }} />
          </button>
          <button
            className="w-10 h-10 rounded-full border backdrop-blur-xl flex items-center justify-center transition-all hover:border-white/30"
            style={{
              background: 'rgba(255, 255, 255, 0.05)',
              borderColor: 'rgba(255, 255, 255, 0.12)',
            }}
          >
            <MenuIcon className="w-5 h-5" style={{ color: 'rgba(255, 255, 255, 0.7)' }} />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto pb-16">
          {/* Profile Content */}
              <div className="flex flex-col items-center pt-8 pb-6 px-4">
                {/* Profile Photo */}
                <div className="relative mb-4">
                  <div 
                    className="w-40 h-40 rounded-full bg-gradient-to-br from-[#00FF88] via-[#4DD4FF] to-[#0066CC] border-0 flex items-center justify-center overflow-hidden relative"
                    style={{ 
                      boxShadow: '0 0 0 2px rgba(255, 255, 255, 0.2)',
                    }}
                  >
                    {profile.photo_file_id ? (
                      <img src={profile.photo_file_id} alt={profile.name || 'User'} className="w-full h-full object-cover" />
                    ) : (
                      <span style={{ color: '#FFFFFF', fontSize: '64px' }}>
                        {(profile.name || 'U').charAt(0).toUpperCase()}
                      </span>
                    )}
                    
                    {/* Plus icon at bottom right */}
                    <div 
                      className="absolute bottom-0 right-0 w-8 h-8 rounded-full border-2 flex items-center justify-center"
                      style={{
                        background: 'rgba(0, 255, 136, 0.9)',
                        borderColor: '#FFFFFF',
                      }}
                    >
                      <span style={{ color: '#FFFFFF', fontSize: '16px', fontWeight: 'bold' }}>+</span>
                    </div>
                  </div>
                </div>

                {/* Name and Roles */}
                <h2 className="mb-3" style={{ color: '#FFFFFF', fontSize: '16px' }}>
                  {profile.name ? `@ ${profile.name}` : '@ Name'}
                </h2>
                <div className="flex flex-wrap gap-2 justify-center mb-4">
                  {profile.roles?.map(role => (
                    <div 
                      key={role}
                      className="px-3 py-1 rounded-full"
                      style={{
                        background: 'rgba(0, 255, 136, 1)',
                        color: '#000000',
                        fontSize: '12px',
                        fontWeight: '500',
                      }}
                    >
                      {ROLE_LABELS[role] || role}
                    </div>
                  ))}
                </div>
                
                {/* Bio */}
                {profile.bio && profile.bio !== 'Не вказано' && (
                  <p className="mb-1" style={{ color: '#FFFFFF', fontSize: '14px' }}>
                    {profile.bio}
                  </p>
                )}
                
                {/* City */}
                {profile.city && profile.city !== 'Не вказано' && (
                  <p className="mb-4" style={{ color: '#FFFFFF', fontSize: '14px' }}>
                    {profile.city}
                  </p>
                )}

                {/* Hashtags */}
                {profile.styles && profile.styles !== 'Не вказано' && (
                  <div className="flex flex-wrap gap-1 justify-center mb-6">
                    {profile.styles.split(',').map((style, idx) => {
                      const tag = style.trim().startsWith('#') ? style.trim() : `#${style.trim()}`;
                      return (
                        <span key={idx} style={{ color: '#FFFFFF', fontSize: '14px' }}>
                          {tag}
                        </span>
                      );
                    })}
                  </div>
                )}

                {/* Stats Row */}
                <div className="flex items-center justify-center mt-4 px-6 py-3"
                     style={{
                       background: 'rgba(255, 255, 255, 0.05)',
                       borderRadius: '12px',
                     }}>
                  {/* Stats with dividers */}
                  <div className="flex gap-6">
                    <div className="text-center">
                      <div style={{ color: '#00FF88', fontSize: '24px', fontWeight: 'bold' }}>
                        {totalWorks}
                      </div>
                      <div style={{ color: 'rgba(255, 255, 255, 0.6)', fontSize: '12px' }}>
                        Робіт
                      </div>
                    </div>
                    <div className="text-center border-l" style={{ borderColor: 'rgba(255, 255, 255, 0.2)', paddingLeft: '24px' }}>
                      <div style={{ color: '#00FF88', fontSize: '24px', fontWeight: 'bold' }}>
                        {totalLikes}
                      </div>
                      <div style={{ color: 'rgba(255, 255, 255, 0.6)', fontSize: '12px' }}>
                        Лайків
                      </div>
                    </div>
                    <div className="text-center border-l" style={{ borderColor: 'rgba(255, 255, 255, 0.2)', paddingLeft: '24px' }}>
                      <div style={{ color: '#00FF88', fontSize: '24px', fontWeight: 'bold' }}>
                        {followers}
                      </div>
                      <div style={{ color: 'rgba(255, 255, 255, 0.6)', fontSize: '12px' }}>
                        Фоловерів
                      </div>
                    </div>
                  </div>
                </div>

                {/* Action Buttons */}
                <div className="flex items-center justify-center gap-2 mt-4">
                  <button
                    onClick={handleFollowToggle}
                    className="px-5 h-10 rounded-full font-semibold transition-all"
                    style={
                      isFollowing
                        ? {
                            // Following: gradient pill with soft outline
                            background: 'linear-gradient(90deg, #14B97A 0%, #22D3EE 100%)',
                            color: '#FFFFFF',
                            border: '1px solid rgba(255,255,255,0.35)',
                            boxShadow:
                              '0 10px 28px rgba(34, 211, 238, 0.25), inset 0 1px 0 rgba(255,255,255,0.2)',
                          }
                        : {
                            // Follow: compact green pill with glow
                            background: '#22C55E',
                            color: '#FFFFFF',
                            boxShadow: '0 10px 28px rgba(34, 197, 94, 0.45)',
                          }
                    }
                  >
                    {isFollowing ? 'Following' : 'Follow'}
                  </button>
                  <button
                    onClick={handleMessageClick}
                    className="w-10 h-10 rounded-2xl border backdrop-blur-xl flex items-center justify-center transition-all hover:border-white/30"
                    style={{
                      background: 'rgba(255, 255, 255, 0.05)',
                      borderColor: 'rgba(255, 255, 255, 0.12)',
                      color: 'rgba(255, 255, 255, 0.7)',
                      boxShadow: '0 6px 18px rgba(0, 0, 0, 0.25)',
                    }}
                  >
                    <MessageCircle className="w-5 h-5" />
                  </button>
                </div>
              </div>

              {/* Portfolio Grid */}
              {portfolios.length > 0 && (
                <div className="mt-6">
                  {/* TikTok-style Grid */}
                  <div className="grid grid-cols-2 gap-1 px-4">
                    {portfolios.map((item, index) => {
                      const showContent = !isLinkHidden(item);

                      if (!showContent) {
                        return (
                          <div
                            key={index}
                            className="w-full h-full bg-gradient-to-br from-black/60 to-black/40 flex items-center justify-center rounded-2xl border border-white/10"
                          >
                            <div className="text-center px-2">
                              <LinkIcon
                                className="w-8 h-8 mx-auto mb-1"
                                style={{ color: 'rgba(255, 255, 255, 0.3)' }}
                              />
                              <p
                                style={{
                                  color: 'rgba(255, 255, 255, 0.3)',
                                  fontSize: '9px',
                                }}
                              >
                                Приховано
                              </p>
                            </div>
                          </div>
                        );
                      }

                      const handleClick = () => {
                        if (onItemClick) {
                          // Allow parent (e.g. marketplace view) to open its own item detail modal
                          onItemClick(item);
                          return;
                        }

                        // Fallback: open swipe-style item card modal
                        setSelectedPortfolioItem(item);
                      };

                      // Non-hidden content: mini marketplace-style card with no inline playback
                      return (
                        <PortfolioMiniCard
                          key={index}
                          item={item}
                          onClick={handleClick}
                        />
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Active Filters Info */}
              {hasActiveFilters && (
                <div className="px-4 mb-6">
                  <div className="p-3 rounded-2xl border backdrop-blur-xl flex items-center justify-between"
                       style={{
                         background: 'rgba(139, 92, 246, 0.1)',
                         borderColor: 'rgba(139, 92, 246, 0.3)',
                       }}>
                    <div className="flex items-center gap-2">
                      <Filter className="w-4 h-4" style={{ color: '#8B5CF6' }} />
                      <span style={{ color: '#8B5CF6', fontSize: '13px' }}>
                        Активні фільтри
                      </span>
                    </div>
                    {onChangeFilters && (
                      <button
                        onClick={onChangeFilters}
                        className="px-3 py-1 rounded-lg transition-all hover:bg-white/5"
                        style={{ color: '#8B5CF6', fontSize: '12px' }}
                      >
                        Змінити
                      </button>
                    )}
                  </div>
                </div>
              )}
        </div>
        {selectedPortfolioItem && (
          <PortfolioSwipeCardModal
            item={selectedPortfolioItem}
            owner={{
              id: profile.telegram_id,
              name: profile.name,
              username: profile.username ?? null,
              avatar: profile.photo_file_id ?? null,
            }}
            onClose={() => setSelectedPortfolioItem(null)}
            onWrite={onWrite}
          />
        )}
    </div>
  );
}

